import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { EqualHeight, EqualHeightElement } from "react-equal-height/clean";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import { useBreakPoints } from "@directv-digital/dds.utils";
import { getAnalyticsAttrs, fireGa4ItemList } from "@directv-digital/dds.services.analytics-service";
import { fusionScrollBarStyles } from "@directv-digital/dds.core.commons";
import { OfferCardsTabTray } from "./offer-cards-tab-tray";

export interface IDtvCardProps {
  id?: string;
  /** Category for filter tab matching */
  category?: string;
  tileTopper?: {
    content?: string;
    backgroundColor?: string;
    color?: string;
  },
  backgroundImage?: {
    desktop?: string;
    tablet?: string;
    mobile?: string;
    alt?: string;
  },
  heading?: string;
  subHeading?: {
    content?: string;
    tooltip?: {
      content?: string;
    };
  },
  primaryCta?: {
    label?: string;
    color?: any;
    [key: string]: any;
  },
  secondaryCta?: {
    label?: string;
    color?: any;
    [key: string]: any;
  },
  cardLegal?: {
    content?: string;
    cta?: {
      label?: string;
      legalUrl?: string;
      position?: any;
      targetUrl?: string;
      target?: string;
      accessibility?: string;
      fontSize?: string;
      color?: string;
    },
  };
}

export interface FilterTab {
  label: string;
  value: string;
}

export type DtvOfferCardsProps = {
  id?: string;
  cards?: IDtvCardProps[];
  variant?: string;
  /** "wrap" (default) = cards wrap into rows; "scroll" = horizontal scroll */
  layout?: "wrap" | "scroll";
  /** Filter tabs config. When provided, renders TabTray above cards. First tab should be "All". */
  filterTabs?: FilterTab[];
  widths?: {
    Cmp2up: {
      xs: string;
      sm: string;
      md: string;
    };
    wide2up: {
      xs: string;
      sm: string;
      lg: string;
    };
  };
};

const styles = {
  threeupContainer: {
    display: "flex",
    alignItems: {
      xs: "center",
      sm: "start"
    },
    flexDirection: {
      xs: "column",
      sm: "row"
    }
  },
  section: {
    padding: {
      xs: "0px 0px 24px 0px",
      sm: "0px 0px 36px 0px",
      md: "0px 0px 32px 0px"
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexDirection: "column",

    flexShrink: 0,
    boxShadow: "0px 2px 20px 0px rgba(0, 0, 0, 0.08)",
    position: "relative",
    borderRadius: "10px",
    background: "#FFF"
  },
  imageContainer: {
    width: "100%",

    flexShrink: 0,
    borderRadius: "10px 10px 0px 0px",
    display: "flex",
    flexDirection: "column"
  },
  tileTopperContainer: {
    height: "31px",
    borderRadius: "10px 10px 0px 0px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  tileTopper: {
    height: "21px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  bodyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: {
      xs: "20px",
      sm: "20px",
      md: "32px"
    },
    flexShrink: 0
  },
  bodyHeaderContainer: {
    margin: {
      xs: "0px 20px",
      sm: "0px 8px",
      md: "0px 22.5px"
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",

    alignSelf: "stretch"
  },
  bodyHeading: {
    color: "#102641",
    textAlign: "center",
    alignSelf: "stretch"
  },
  bodySubHeading: {
    color: "#102641",
    display: "inline"
  },
  bodyButtonsContainer: {
    display: "inline",
    textAlign: "center",
    margin: {
      xs: "0px 20px",
      sm: "0px 13.5px",
      md: "0px 22.5px"
    },
    "& div": {
      width: "100%"
    },
    alignSelf: "stretch"
  },
  bodyPrimaryButton: {
    height: {
      xs: "36px",
      sm: "36px",
      md: "56px"
    },
    fontSize: {
      xs: "14px",
      sm: "14px",
      md: "16px"
    },
    marginBottom: {
      xs: "20px",
      sm: "16px",
      md: "16px"
    },
    padding: {
      xs: "0px 16px",
      sm: "0px 16px",
      md: "0px 24px"
    }
  },
  bodySecondaryButton: {
    width: "100%",
    height: {
      xs: "36px",
      sm: "36px",
      md: "56px"
    },
    fontSize: {
      xs: "14px",
      sm: "14px",
      md: "16px"
    },
    marginBottom: {
      xs: "20px",
      sm: "16px",
      md: "16px"
    },
    padding: {
      xs: "0px 16px",
      sm: "0px 16px",
      md: "0px 24px"
    }
  },
  bodyHelperText: {
    color: "#555",
    textAlign: "center"
  }
};

export function DtvOfferCards(props: DtvOfferCardsProps) {
  try {
    const containerRef = useRef<HTMLElement>(null);
    // Generated by Copilot- Start - 2026-03-27 16:20:41
    const { isMobile } = useBreakPoints();
    // Generated by Copilot- End - 2026-03-27 16:20:41

    const allCards = props && props?.cards && props?.cards?.length > 0 && props?.cards;
    const widths = props?.widths;
    const layout = props?.layout ?? "wrap";
    const hasFilterTabs = props?.filterTabs && props.filterTabs.length > 0;

    // Filter tabs state
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    const cards = useMemo(() => {
      if (!allCards) {
        return allCards;
      }
      if (!hasFilterTabs) {
        return allCards;
      }
      const filterValue = props.filterTabs![activeTabIndex]?.value || "all";
      if (filterValue === "all") {
        return allCards;
      }
      return allCards.filter(card => card.category === filterValue);
    }, [allCards, hasFilterTabs, props.filterTabs, activeTabIndex]);

    const tabTrayTabs = useMemo(() => {
      if (!hasFilterTabs) {
        return [];
      }
      return props.filterTabs!.map(t => ({ label: t.label }));
    }, [hasFilterTabs, props.filterTabs]);

    const handleFilterTabChange = useCallback((index: number) => {
      setActiveTabIndex(index);
    }, []);

    const defaultWidths = {
      Cmp2up: {
        xs: "332px",
        sm: "226px",
        md: "373px"
      },
      wide2up: {
        xs: "334px",
        sm: "342px",
        lg: "505px"
      }
    };

    const finalWidths = widths || defaultWidths;

    const twoUpVariant = props?.variant === "wide2upoffercards";
    const isScroll = layout === "scroll";
    const [isOverflowing, setIsOverflowing] = useState(false);

    const checkOverflow = () => {
      if (containerRef.current) {
        const element = containerRef.current as HTMLElement;
        setIsOverflowing(element?.scrollWidth > element?.clientWidth);
      }
    };

    const handleCtaClickEvents = (
      title: string | undefined,
      coupon: string | undefined,
      index: number
    ) => {
      fireGa4ItemList("select_item", [
        {
          item_id: title?.toLowerCase(),
          item_name: title?.toLowerCase(),
          item_variant: "",
          affiliation: "",
          coupon: coupon?.toLowerCase(),
          discount: "",
          index,
          item_list_id: title?.toLowerCase(),
          item_list_name: title?.toLowerCase(),
          price: "",
          quantity: 1
        }
      ]);
    };

    const viewItemListFired = useRef(false);
    useEffect(() => {
      if (viewItemListFired.current || !allCards || allCards.length === 0) {
        return;
      }
      viewItemListFired.current = true;
      const items = allCards.map((card, i) => ({
        item_id: card?.tileTopper?.content?.toLowerCase(),
        item_name: card?.tileTopper?.content?.toLowerCase(),
        item_variant: "",
        affiliation: "",
        coupon: card?.heading?.toLowerCase(),
        discount: "",
        index: i,
        item_list_id: card?.tileTopper?.content?.toLowerCase(),
        item_list_name: card?.tileTopper?.content?.toLowerCase(),
        price: "",
        quantity: 1
      }));
      fireGa4ItemList("view_item_list", items);
    }, [allCards]);

    useEffect(() => {
      checkOverflow();
      window.addEventListener("resize", checkOverflow);
      return () => {
        window.removeEventListener("resize", checkOverflow);
      };
    }, []);

    const cardGap = twoUpVariant ? {
      xs: "30px",
      sm: "20px"
    } : {
      xs: "24px",
      sm: "16px",
      md: "20px"
    };

    const containerSx = {
      ...styles.threeupContainer,
      gap: cardGap,
      ...(isScroll ?
        {
          margin: !twoUpVariant ? "0px 20px" : undefined,
          overflowX: "auto",
          ...fusionScrollBarStyles(),
          flexShrink: 0,
          justifyContent: isOverflowing ? "start" : "center"
        } :
        {
          flexWrap: "wrap",
          justifyContent: "center",
          maxWidth: twoUpVariant ? "none" : {
            sm: "710px",
            md: "1159px"
          },
          margin: !twoUpVariant ? {
            xs: "0px 20px",
            sm: "0 auto"
          } : undefined
        }
      )
    };

    const renderCard = (card: IDtvCardProps, index: number) => (
      <DtvBox
        key={card?.id ?? `card-${index}`}
        sx={{
          ...styles.section,
          width: twoUpVariant ?
            finalWidths.wide2up :
            finalWidths.Cmp2up,
          minWidth: twoUpVariant ?
            finalWidths.wide2up : undefined,
          ...(isScroll && { flexShrink: 0 }),
          gap: twoUpVariant ?
            { xs: "15px" } :
            {
 xs: "18px",
sm: "20px",
md: "15px"
}
        }}
      >
        <DtvBox
          sx={{
            ...styles.imageContainer,
            height: !twoUpVariant ? "206px" : undefined
          }}
        >
          {card?.tileTopper?.content && <DtvBox
            sx={{
              ...styles.tileTopperContainer,
              background: card?.tileTopper?.backgroundColor
            }}
          >
            <DtvText
              content={card?.tileTopper?.content}
              variant="eyebrowregular"
              sx={{
                ...styles.tileTopper,
                color: card?.tileTopper?.color
              }}
            />
          </DtvBox>}
          <DtvBox
            sx={{
              width: "100%",
              flexGrow: 1,
              height: twoUpVariant ? "237px" : undefined
            }}
          >
            <picture>
              <source
                media={`(min-width: ${DtvTheme.breakpoints.values.md}px)`}
                srcSet={card?.backgroundImage?.desktop}
              />
              <source
                media={`(min-width: ${DtvTheme.breakpoints.values.sm}px)`}
                srcSet={card?.backgroundImage?.tablet}
              />
              <source
                srcSet={card?.backgroundImage?.mobile}
                media={
                  `(min-width: ${(DtvTheme.breakpoints.values as Record<string, number>).xs}px)`
                }
              />
              <img
                alt={card?.backgroundImage?.alt || card?.id}
                id={`dtv-${card?.id}-background`}
                loading={"lazy"}
                src={card?.backgroundImage?.desktop}
                srcSet={card?.backgroundImage?.desktop}
                style={{
                  // block, not inline: the inline baseline gap made the box ~5px taller than the slot, so cover cropped the sides
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: twoUpVariant ? "fill" : "cover",
                  borderRadius: card?.tileTopper?.content ? "0px" : "10px 10px 0px 0px"
                }}
              />
            </picture>
          </DtvBox>
        </DtvBox>
        <DtvBox sx={{ ...styles.bodyContainer }}>
          <DtvBox
            sx={{
              ...styles.bodyHeaderContainer,
              gap: twoUpVariant ?
                {
 xs: "4px",
sm: "0px"
} :
                {
 xs: "4px",
sm: "16px",
md: "4px"
}
            }}
          >
            <DtvBox
              sx={{
                display: "inline",
                textAlign: "center"
              }}
            >
              <EqualHeightElement
                disable={isMobile}
                name="body-header-container"
              >
                <DtvText
                  content={card?.heading}
                  sx={{ ...styles.bodyHeading }}
                  variant="subhead2medium"
                />
              </EqualHeightElement>
            </DtvBox>
            <DtvBox
              sx={
                twoUpVariant ?
                  {
                      display: "block",
                      maxWidth: {
 lg: "337px",
xs: "282px"
},
                      width: {
 lg: "337px",
xs: "282px"
},
                      margin: "0 auto",
                      textAlign: "left",
                      paddingY: "15px"
                    } :
                  {
                      display: "inline"
                    }
              }
            >
              <EqualHeightElement
                disable={isMobile}
                name="offer-cards-subheading"
              >
                <DtvText
                  variant="body3"
                  content={
                    card?.subHeading?.content?.includes?.(
                      "[[tooltip]]"
                    ) ?
                      card?.subHeading?.content?.replace?.(
                          "[[tooltip]]",
                          card?.subHeading?.tooltip?.content
                        ) :
                      card?.subHeading?.content
                  }
                  sx={{
                    ...styles.bodySubHeading,
                    "& span": {
                      fontSize: "15px !important",
                      textAlign: twoUpVariant ? "auto" : "center"
                    },
                    "& ul, ol": {
                      margin: "0",
                      padding: !twoUpVariant ?
                        "1em 0 1em 40px" : undefined,
                      textAlign: "left"
                    },
                    ...(twoUpVariant && {
                      "& p": {
                        margin: "8px 0 0 0"
                      }
                    })
                  }}
                />
              </EqualHeightElement>
            </DtvBox>
          </DtvBox>
          <DtvBox sx={{ ...styles.bodyButtonsContainer }}>
            <EqualHeightElement
              disable={isMobile}
              name="offer-cards-buttons"
            >
              {card?.primaryCta?.label && (
                <DtvButton
                  label={card?.primaryCta?.label}
                  sx={{
                    ...styles.bodyPrimaryButton,
                    width: twoUpVariant ?
                      {
 xs: "202px",
lg: "332px"
} :
                      "100%",
                    color: card?.primaryCta?.color
                  }}
                  {...card?.primaryCta}
                  googleAnalyticsData={getAnalyticsAttrs(card?.primaryCta?.label, 'panel_offer card', card?.primaryCta?.targeturl)}
                  onClick={() => {
                    handleCtaClickEvents(
                      card?.tileTopper?.content,
                      card?.heading,
                      index
                    );
                  }}
                />
              )}
              {card?.secondaryCta?.label && (
                <DtvButton
                  label={card?.secondaryCta?.label}
                  mode="dark"
                  variant="uxsecondary"
                  sx={{
                    ...styles.bodySecondaryButton,
                    color: card?.secondaryCta?.color
                  }}
                  {...card?.secondaryCta}
                  googleAnalyticsData={getAnalyticsAttrs(card?.secondaryCta?.label, 'panel_offer card', card?.secondaryCta?.targeturl)}
                  onClick={() => {
                    handleCtaClickEvents(
                      card?.tileTopper?.content,
                      card?.heading,
                      index
                    );
                  }}
                />
              )}
            </EqualHeightElement>
            <EqualHeightElement
              disable={isMobile}
              name="offer-card-legal"
            >
              <DtvText
                content={card?.cardLegal?.content}
                variant="body3"
                cta={{
                  label: card?.cardLegal?.cta?.label || "",
                  legalUrl: card?.cardLegal?.cta?.legalUrl,
                  position: card?.cardLegal?.cta?.position,
                  targeturl: card?.cardLegal?.cta?.targetUrl,
                  sx: {
                    marginBottom: "2px",
                    fontSize: card?.cardLegal?.cta?.fontSize,
                    color: card?.cardLegal?.cta?.color
                  }
                }}
                sx={{
                  ...styles.bodyHelperText,
                  // WAT-11565: this legal cta renders as <button> now, not <a>.
                  "a:focus, button:focus": {
                    outline: "2px solid #2E70BD"
                  }
                }}
              />
            </EqualHeightElement>
          </DtvBox>
        </DtvBox>
      </DtvBox>
    );

    // Figma annotation: "Navigation will bleed on right of viewport.
    // User can scroll right to left on touch similar to tabs on TV guide pages."
    // Tray is always the same size. When viewport is too narrow, it overflows
    // and the user can scroll horizontally via touch.
    const filterTabsEl = hasFilterTabs ? (
      <DtvBox sx={{
        marginBottom: {
 xs: "24px",
sm: "24px",
md: "32px"
},
        overflowX: "auto",
        scrollPaddingInline: "16px",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        paddingLeft: "13px",
        paddingRight: "13px",
        // Center the tray via auto margins when it fits.
        // When tray overflows, auto margins collapse to 0 → anchors left.
        "& .MuiTabs-root": {
          margin: "0 auto"
        }
      }}>
        <OfferCardsTabTray
          ariaLabel="Filter cards by category"
          defaultSelectedTab={0}
          handleChange={handleFilterTabChange}
          id={`${props?.id || "dtv-offer-cards"}-filter`}
          tabs={tabTrayTabs}
        />
      </DtvBox>
    ) : null;

    // In wrap mode, chunk cards into rows of 3 so EqualHeight only applies per row
    if (!isScroll && cards && cards.length > 3) {
      const CARDS_PER_ROW = 3;
      const rows: IDtvCardProps[][] = [];
      for (let i = 0; i < cards.length; i += CARDS_PER_ROW) {
        rows.push(cards.slice(i, i + CARDS_PER_ROW));
      }
      return (
        <>
          {filterTabsEl}
          <DtvBox
            id={props?.id || "dtv-offer-cards"}
            ref={containerRef}
            sx={containerSx}
          >
            {/* Generated by Copilot- Start - 2026-04-19 23:49:49 */}
            {rows.map((row, rowIdx) => (
              <EqualHeight key={`${activeTabIndex}-${rowIdx}`}>
                <DtvBox
                  sx={{
                    display: "flex",
                    flexDirection: {
 xs: "column",
sm: "row"
},
                    alignItems: {
 xs: "center",
sm: "start"
},
                    justifyContent: "center",
                    gap: cardGap,
                    width: "100%"
                  }}
                >
                  {row.map((card, colIdx) => renderCard(card, rowIdx * CARDS_PER_ROW + colIdx))}
                </DtvBox>
              </EqualHeight>
            ))}
            {/* Generated by Copilot- End - 2026-04-19 23:49:49 */}
          </DtvBox>
        </>
      );
    }

    // Scroll mode or ≤3 cards: original single EqualHeight
    // Generated by Copilot- Start - 2026-04-19 23:49:49
    return (
      <>
        {filterTabsEl}
        <EqualHeight key={activeTabIndex}>
          <DtvBox
            id={props?.id || "dtv-offer-cards"}
            ref={containerRef}
            sx={containerSx}
          >
            {cards && cards?.map((card, index) => renderCard(card, index))}
          </DtvBox>
        </EqualHeight>
        {/* Generated by Copilot- End - 2026-04-19 23:49:49 */}
      </>
    );
  } catch (error) {
    return <ErrorDisplay component="DTV Offer Cards" error={error} />;
  }
}
